import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Users, Shield, Target, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

interface EvaluatorSection {
  name: string;
  role: string;
  sections: {
    title: string;
    icon: React.ReactNode;
    content: {
      subtitle: string;
      text: string;
    }[];
  }[];
}

const evaluators: EvaluatorSection[] = [
  {
    name: "Sophia Carter",
    role: "Peer",
    sections: [
      {
        title: "Communication and Collaboration Strategies",
        icon: <MessageSquare className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Conversation Starter",
            text: "How do you think we could improve how we collaborate on cross-functional projects?"
          },
          {
            subtitle: "Tip",
            text: "Sophia prefers concise and actionable updates during meetings; prepare clear bullet points for discussions."
          },
          {
            subtitle: "Listening Practice",
            text: "Repeat back Sophia's key points to confirm understanding and show engagement."
          }
        ]
      },
      {
        title: "Building Trust and Strengthening Relationships",
        icon: <Users className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Empathy Builder",
            text: "Consider Sophia's workload during project deadlines and offer support when appropriate."
          },
          {
            subtitle: "Trust Strategy",
            text: "Deliver on promises, such as meeting deadlines for shared tasks."
          },
          {
            subtitle: "Recognition Practice",
            text: "Publicly acknowledge Sophia's contributions during team retrospectives."
          }
        ]
      },
      {
        title: "Constructive Feedback and Conflict Resolution",
        icon: <Shield className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Feedback Request",
            text: "What's one thing I could adjust to make our collaboration smoother?"
          },
          {
            subtitle: "Conflict Technique",
            text: "Address disagreements constructively, e.g., \"Let's explore a middle ground for our project timeline.\""
          },
          {
            subtitle: "Reflective Prompt",
            text: "Did I effectively address Sophia's concerns during our last meeting?"
          }
        ]
      },
      {
        title: "Leveraging Strengths and Shared Goals",
        icon: <Target className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Shared Goal",
            text: "Collaborate to streamline project workflows."
          },
          {
            subtitle: "Strength Amplifier",
            text: "Sophia's organizational skills can help in creating structured project plans."
          },
          {
            subtitle: "Balance Focus",
            text: "Encourage Sophia to pair strategic planning with actionable next steps."
          }
        ]
      }
    ]
  },
  {
    name: "Liam Johnson",
    role: "Line Manager",
    sections: [
      {
        title: "Communication and Collaboration Strategies",
        icon: <MessageSquare className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Conversation Starter",
            text: "Are there ways I could make updates more efficient for you?"
          },
          {
            subtitle: "Tip",
            text: "Liam values proactive updates; send a brief summary of progress before formal reviews."
          },
          {
            subtitle: "Listening Practice",
            text: "Take notes during discussions and follow up on action items."
          }
        ]
      },
      {
        title: "Building Trust and Strengthening Relationships",
        icon: <Users className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Empathy Builder",
            text: "Show understanding of Liam's strategic vision by aligning your efforts with organizational priorities."
          },
          {
            subtitle: "Trust Strategy",
            text: "Be transparent about challenges and provide realistic solutions during check-ins."
          },
          {
            subtitle: "Recognition Practice",
            text: "Thank Liam for mentoring opportunities, e.g., \"Your feedback during my presentation preparation was invaluable.\""
          }
        ]
      },
      {
        title: "Constructive Feedback and Conflict Resolution",
        icon: <Shield className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Feedback Request",
            text: "What's one skill you think I should develop to contribute more effectively to the team?"
          },
          {
            subtitle: "Conflict Technique",
            text: "Approach disagreements by focusing on shared organizational objectives."
          },
          {
            subtitle: "Reflective Prompt",
            text: "Am I presenting my ideas in a way that aligns with Liam's expectations?"
          }
        ]
      },
      {
        title: "Leveraging Strengths and Shared Goals",
        icon: <Target className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Shared Goal",
            text: "Support the department's strategic initiatives through improved project outcomes."
          },
          {
            subtitle: "Strength Amplifier",
            text: "Noah's big-picture thinking can inspire innovative approaches to challenges."
          },
          {
            subtitle: "Balance Focus",
            text: "Translate Noah's strategic vision into actionable steps for your team."
          }
        ]
      }
    ]
  },
  {
    name: "Ava Martinez",
    role: "Direct Report",
    sections: [
      {
        title: "Communication and Collaboration Strategies",
        icon: <MessageSquare className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Conversation Starter",
            text: "How can I support you better in achieving your goals?"
          },
          {
            subtitle: "Tip",
            text: "Ava appreciates regular check-ins; schedule bi-weekly one-on-one meetings to discuss her progress."
          },
          {
            subtitle: "Listening Practice",
            text: "Create a safe space for Ava to share challenges by actively listening and asking open-ended questions."
          }
        ]
      },
      {
        title: "Building Trust and Strengthening Relationships",
        icon: <Users className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Empathy Builder",
            text: "Understand Ava's career aspirations and provide growth opportunities."
          },
          {
            subtitle: "Trust Strategy",
            text: "Delegate tasks with clear expectations and provide constructive feedback."
          },
          {
            subtitle: "Recognition Practice",
            text: "Highlight Ava's contributions during team meetings, e.g., \"Ava's creativity was key to this project's success.\""
          }
        ]
      },
      {
        title: "Constructive Feedback and Conflict Resolution",
        icon: <Shield className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Feedback Request",
            text: "Is there anything I could do differently to better support your work?"
          },
          {
            subtitle: "Conflict Technique",
            text: "Address misunderstandings promptly and focus on resolving issues collaboratively."
          },
          {
            subtitle: "Reflective Prompt",
            text: "Am I providing Ava with enough guidance and support without micromanaging?"
          }
        ]
      },
      {
        title: "Leveraging Strengths and Shared Goals",
        icon: <Target className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Shared Goal",
            text: "Ensure project deadlines are met while fostering Ava's professional development."
          },
          {
            subtitle: "Strength Amplifier",
            text: "Ava's creativity can be leveraged for brainstorming innovative solutions."
          },
          {
            subtitle: "Balance Focus",
            text: "Help Ava align her individual goals with the team's objectives."
          }
        ]
      }
    ]
  },
  {
    name: "Noah Williams",
    role: "Indirect Line Manager",
    sections: [
      {
        title: "Communication and Collaboration Strategies",
        icon: <MessageSquare className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Conversation Starter",
            text: "Are there any areas where you think I could better align with the department's objectives?"
          },
          {
            subtitle: "Tip",
            text: "Noah values data-driven communication; present updates with relevant metrics and outcomes."
          },
          {
            subtitle: "Listening Practice",
            text: "Summarize Noah's feedback to ensure alignment with broader goals."
          }
        ]
      },
      {
        title: "Building Trust and Strengthening Relationships",
        icon: <Users className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Empathy Builder",
            text: "Acknowledge Noah's strategic challenges and offer solutions to support departmental goals."
          },
          {
            subtitle: "Trust Strategy",
            text: "Be proactive in providing updates on projects that align with Noah's priorities."
          },
          {
            subtitle: "Recognition Practice",
            text: "Share how Noah's guidance has positively impacted your team's performance."
          }
        ]
      },
      {
        title: "Constructive Feedback and Conflict Resolution",
        icon: <Shield className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Feedback Request",
            text: "What's one area where I could improve to better align with your expectations?"
          },
          {
            subtitle: "Conflict Technique",
            text: "Frame disagreements as opportunities for collaboration, e.g., \"How can we address this challenge together?\""
          },
          {
            subtitle: "Reflective Prompt",
            text: "Am I aligning my contributions effectively with Noah's strategic direction?"
          }
        ]
      },
      {
        title: "Leveraging Strengths and Shared Goals",
        icon: <Target className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Shared Goal",
            text: "Support the department's strategic initiatives through improved project outcomes."
          },
          {
            subtitle: "Strength Amplifier",
            text: "Noah's big-picture thinking can inspire innovative approaches to challenges."
          },
          {
            subtitle: "Balance Focus",
            text: "Translate Noah's strategic vision into actionable steps for your team."
          }
        ]
      }
    ]
  },
  {
    name: "Emma Davis",
    role: "Peer",
    sections: [
      {
        title: "Communication and Collaboration Strategies",
        icon: <MessageSquare className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Conversation Starter",
            text: "What's one way we could work together more effectively on future projects?"
          },
          {
            subtitle: "Tip",
            text: "Emma thrives in collaborative environments; focus on brainstorming and shared problem-solving."
          },
          {
            subtitle: "Listening Practice",
            text: "Engage Emma by acknowledging her ideas and building on them during discussions."
          }
        ]
      },
      {
        title: "Building Trust and Strengthening Relationships",
        icon: <Users className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Empathy Builder",
            text: "Show interest in Emma's challenges and offer support when needed."
          },
          {
            subtitle: "Trust Strategy",
            text: "Be consistent in communication and follow through on collaborative tasks."
          },
          {
            subtitle: "Recognition Practice",
            text: "Acknowledge Emma's contributions directly, e.g., \"Your input was crucial in refining this proposal.\""
          }
        ]
      },
      {
        title: "Constructive Feedback and Conflict Resolution",
        icon: <Shield className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Feedback Request",
            text: "Is there anything I could do to make our collaboration more seamless?"
          },
          {
            subtitle: "Conflict Technique",
            text: "Use neutral language during disagreements, focusing on solutions rather than assigning blame."
          },
          {
            subtitle: "Reflective Prompt",
            text: "Am I giving Emma enough space to share her ideas during discussions?"
          }
        ]
      },
      {
        title: "Leveraging Strengths and Shared Goals",
        icon: <Target className="w-5 h-5 text-primary" />,
        content: [
          {
            subtitle: "Shared Goal",
            text: "Improve team workflows for greater efficiency."
          },
          {
            subtitle: "Strength Amplifier",
            text: "Emma's attention to detail can enhance project quality."
          },
          {
            subtitle: "Balance Focus",
            text: "Collaborate with Emma to balance her focus on details with overall team progress."
          }
        ]
      }
    ]
  }
];

export function EvaluatorsView() {
  const [showMore, setShowMore] = useState(false);
  const { currentUser } = useAuth();
  const [evaluatorsData, setEvaluatorsData] = useState<{
    evaluators: Array<{
      name: string;
      role: string;
      sections: Array<{
        title: string;
        content: Array<{
          subtitle: string;
          text: string;
        }>;
      }>;
    }>;
  } | null>(null);
  const [sentimentData, setSentimentData] = useState<{
    score: number;
    description: string;
  } | null>(null);
  const [coherenceData, setCoherenceData] = useState<{
    score: number;
    description: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadReportData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          
          if (data.evaluators_view_evaluators_detail) {
            try {
              const parsedData = JSON.parse(data.evaluators_view_evaluators_detail);
              setEvaluatorsData(parsedData);
            } catch (error) {
              console.error('Error parsing evaluators detail data:', error);
              setEvaluatorsData(null);
            }
          }
          
          if (data.evaluators_view_sentiment_score) {
            const parsedData = JSON.parse(data.evaluators_view_sentiment_score);
            setSentimentData({
              score: parsedData.score,
              description: parsedData.description
            });
          }
          
          if (data.evaluators_view_coherence_score) {
            const parsedData = JSON.parse(data.evaluators_view_coherence_score);
            setCoherenceData({
              score: parsedData.score,
              description: parsedData.description
            });
          }
        }
      } catch (error) {
        console.error('Error loading report data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadReportData();
  }, [currentUser]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6"
    >
      {/* Sentiment and Coherence Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {/* Sentiment Score */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Sentiment Analysis
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
            </div>
          ) : !sentimentData ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                Sentiment analysis will be available once your report is generated.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative pt-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-500">-100%</span>
                  <span className="text-sm font-medium text-gray-500">0%</span>
                  <span className="text-sm font-medium text-gray-500">+100%</span>
                </div>
                <div className="overflow-hidden h-2 bg-gray-200 rounded">
                  <div 
                    className={`h-full rounded transition-all duration-500 ${
                      sentimentData.score >= 0 ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    style={{ 
                      width: `${Math.abs(sentimentData.score) / 2}%`,
                      marginLeft: sentimentData.score >= 0 ? '50%' : `${50 - Math.abs(sentimentData.score)}%`
                    }}
                  />
                  <div className="absolute top-0 left-1/2 h-2 w-0.5 bg-gray-400" />
                </div>
                <div className="mt-2 text-center">
                  <span className={`text-lg font-bold ${
                    sentimentData.score >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {sentimentData.score > 0 ? '+' : ''}{sentimentData.score}%
                  </span>
                  <p className="text-sm text-gray-600 mt-2">
                    {sentimentData.description}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Coherence Score */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Coherence Score</h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
            </div>
          ) : !coherenceData ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                Coherence analysis will be available once your report is generated.
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[calc(100%-2rem)]">
              <div className="text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                {coherenceData.score}%
              </div>
              <p className="text-sm text-gray-600 mt-4 text-center">
                {coherenceData.description}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">How to work with your evaluators</h2>
      </div>

      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          In this section, you'll uncover personalized strategies to collaborate better with your evaluators. By understanding their perspectives and preferences, you can take meaningful steps toward strengthening each relationship and fostering a more productive and harmonious working dynamic.
        </p>
        
        <motion.div
          initial={false}
          animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>Here's a brief description of how each element can help you make the most of the relation with each evaluator:</p>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Communication and Collaboration Strategies</h4>
              <p>This subsection helps you build effective communication channels and foster better collaboration with each evaluator.</p>
              <ul className="mt-2 ml-4 space-y-2">
                <li><strong>Conversation Starter:</strong> Offers a prompt to engage evaluators in meaningful dialogue about improving communication and teamwork.</li>
                <li><strong>Tip:</strong> Provides actionable advice on tailoring your communication style to the evaluator's preferences.</li>
                <li><strong>Listening Practice:</strong> Suggests techniques to demonstrate active listening, ensuring your evaluator feels heard and understood.</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Building Trust and Strengthening Relationships</h4>
              <p>Learn how to cultivate trust and deepen your professional relationships with evaluators.</p>
              <ul className="mt-2 ml-4 space-y-2">
                <li><strong>Empathy Builder:</strong> Encourages understanding and consideration of the evaluator's challenges or workload to show mutual respect.</li>
                <li><strong>Trust Strategy:</strong> Focuses on delivering on commitments and maintaining transparency to foster reliability.</li>
                <li><strong>Recognition Practice:</strong> Guides you on acknowledging the evaluator's contributions, reinforcing positive rapport.</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Constructive Feedback and Conflict Resolution</h4>
              <p>Enhance your ability to handle feedback constructively and resolve conflicts diplomatically.</p>
              <ul className="mt-2 ml-4 space-y-2">
                <li><strong>Feedback Request:</strong> Proposes specific questions to gather actionable feedback and improve collaboration.</li>
                <li><strong>Conflict Technique:</strong> Suggests approaches to address disagreements professionally, focusing on shared goals.</li>
                <li><strong>Reflective Prompt:</strong> Encourages self-assessment of interactions to identify improvement opportunities in resolving issues.</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Leveraging Strengths and Shared Goals</h4>
              <p>Maximize the strengths of your evaluators and align on shared objectives for collective success.</p>
              <ul className="mt-2 ml-4 space-y-2">
                <li><strong>Shared Goal:</strong> Identifies mutual objectives that can enhance team synergy and productivity.</li>
                <li><strong>Strength Amplifier:</strong> Highlights specific strengths of the evaluator that you can leverage for improved outcomes.</li>
                <li><strong>Balance Focus:</strong> Offers insights into maintaining a balance between the evaluator's strengths and overall goals to ensure effective collaboration.</li>
              </ul>
            </div>
          </div>
        </motion.div>
        
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
        >
          <span>{showMore ? 'Read less' : 'Read more'}</span>
          <motion.svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: showMore ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </motion.svg>
        </button>
      </div>

      <div className="space-y-16">
        {!evaluatorsData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-50 rounded-xl p-8 text-center"
          >
            <div className="flex justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Evaluator Details Not Available
            </h3>
            <p className="text-gray-600 max-w-lg mx-auto">
              The evaluator details will be available once your report is generated.
            </p>
          </motion.div>
        ) : (
          evaluatorsData.evaluators.map((evaluator, index) => (
          <motion.div
            key={evaluator.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transform hover:shadow-xl transition-all duration-300"
          >
            <div className="relative overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-8">
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{evaluator.name}</h3>
                  <p className="text-gray-600 text-lg">{evaluator.role}</p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 opacity-60" />
              </div>
            </div>

            <div className="p-8 space-y-10">
              {evaluator.sections.map((section, sectionIndex) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + sectionIndex * 0.1 }}
                  className="space-y-4"
                >
                  <div className="flex items-center space-x-3 bg-gradient-to-r from-primary/5 to-transparent p-3 rounded-lg">
                    {section.icon}
                    <h4 className="text-lg font-bold text-gray-900">{section.title}</h4>
                  </div>

                  <div className="grid gap-4 ml-7">
                    {section.content.map((item, itemIndex) => (
                      <motion.div
                        key={itemIndex}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + itemIndex * 0.1 }}
                        className="bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow duration-300 border border-gray-100"
                      >
                        <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                          <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                          {item.subtitle}
                        </h5>
                        <p className="text-gray-600 leading-relaxed">{item.text}</p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )))}
      </div>
    </motion.div>
  );
}